import React from 'react'
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '20px',
    backgroundColor: '#fafafa',
  },
  container: {
    marginTop: '50px',
  },
  titleContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: 'fit-content',
    marginBottom: '20px',
    '& h5': {
      color: theme.palette.primary.main,
      position: 'relative',
    },
  },
  titleBackground: {
    position: 'absolute',
    top: '15px',
    left: '10px',
    width: '90%',
    backgroundColor: theme.palette.secondary.main,
    height: '18px',
    zIndex: -1,
  },
  icon: {
    marginRight: '20px',
    height: 'auto',
    minWidth: '40px',
  },
  features: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: '20px',
    '@media (max-width: 420px)': {
      flexDirection: 'column',
    },
  },
  feature: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    minWidth: '40%',
    maxWidth: '50%',
    marginBottom: '20px',
    padding: '20px',
    alignItems: 'center',
    '@media (max-width: 420px)': {
      minWidth: '100%',
      maxWidth: '100%',
    },
  },
  featureTitle: {
    marginBottom: '20px',
    fontSize: '16px',
    color: '#585858',
  },
  bullet: {
    margin: '20px 0',
  },
  heading: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.primary.main,
  },
  panel: {
    marginTop: '5px',
  },
  dataProvider: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    '& >p': {
      marginRight: '10px',
    },
  },
  subtitle: {
    fontSize: '18px',
    margin: '20px 0 10px 0',
  },
  title2: {
    fontSize: '20px',
    margin: '20px 0 10px 0',
  },
}))

function SeoText(): JSX.Element {
  const classes = useStyles({})

  return (
    <Box className={classes.root} id="read_more">
      <div className={classes.dataProvider}>
        <Typography variant="body1">Data provided by:</Typography>
        <img width={220} height={55} src="/svg/loanscan-logo.svg" alt="loanscan" />
      </div>
      <div className={classes.container}>
        <div className={classes.titleContainer}>
          <div className={classes.titleBackground} />
          <Typography variant="h5">FAQ</Typography>
        </div>
        <Accordion square classes={{ root: classes.panel }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography variant="subtitle2" className={classes.heading}>
              How to earn interest on crypto?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              To earn interest on your crypto you need to supply your assets to one of the loan
              providers. If you don’t have any crypto assets you can buy them on a cryptocurrency
              exchange. Moreover, for some providers you’ll need to sign-up and upload identity
              documents before you are eligible to earn interest.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion square classes={{ root: classes.panel }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel2a-content"
            id="panel2a-header"
          >
            <Typography variant="subtitle2" className={classes.heading}>
              What are stablecoins?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              Next to cryptocurrencies like Ether and Bitcoin you can also lend so called
              Stablecoins. Stablecoins are digital currencies pegged 1:1 to US dollars. The most
              famous stablecoins are Tether, USDC, DAI, Paxos and BUSD. They are convenient to
              invest because your principal is denominated in dollars, which is a stable currency.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion square classes={{ root: classes.panel }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel3a-content"
            id="panel3a-header"
          >
            <Typography variant="subtitle2" className={classes.heading}>
              Why are interest rates so high?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              Interest rates differ from one asset to another and are based on supply and demand.
              When there are lots of people that want to supply a specific crypto asset and there
              aren’t many people that want to borrow it, interest rates will be low. Conversely,
              when there are lots of people that want to borrow, but there aren’t many people that
              want to lend a given crypto asset, then interest rates will be high.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion square classes={{ root: classes.panel }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel4a-content"
            id="panel4a-header"
          >
            <Typography variant="subtitle2" className={classes.heading}>
              How is the interest paid to me?
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1">
              Depending on the provider, it&apos;s usually paid into the wallet of your savings
              account. You&apos;re usually paid in the same currency as the currency you’re earning
              interest on. For example, if you have one Bitcoin in a savings account, you’ll receive
              interest on that Bitcoin, which will be paid in Bitcoin in your savings account. The
              frequency of the interest payment will vary per provider, however, it’s usually
              monthly.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </div>
      <div className={classes.container}>
        <div className={classes.titleContainer}>
          <div className={classes.titleBackground} />
          <Typography variant="h5">How do crypto savings accounts work</Typography>
        </div>
        <div>
          <Typography variant="body1">
            Crypto savings accounts work in a similar way to normal bank savings accounts. In a
            nutshell, you lend money to an institution, which lends your assets to borrowers in need
            of liquidity. However, these loans are relatively secure since the loan providers ask
            the borrowers to deposit crypto assets themselves, as security for the loan. Most
            providers ask for a ‘loan-to-value’ ratio of 50% meaning that if a borrower wants $1000
            they’ll need to deposit $2000 worth of e.g Bitcoin as security for the loan.
          </Typography>
          <Typography variant="h5" className={classes.title2}>
            Who are these providers lending my assets to?
          </Typography>
          <Typography variant="body1">
            Broadly spoken, these are institutions and sometimes individuals that hold
            cryptocurrency and need short-term liquidity but don’t want to sell their
            cryptocurrency. To give a few examples of the type of companies using crypto-backed
            loans:
            <ul>
              <li>
                <b>Cryptocurrency Miners</b>: Need financing to cover their operating expenditures
                (pay employees, electricity costs etc.).{' '}
              </li>
              <li>
                <b>Traders</b>: Want to seize arbitrage opportunities and need short-term liquidity
                to execute profitable trades.
              </li>
              <li>
                <b>Hedge funds</b>: Have invested in ICO’s or other digital assets and want to
                leverage their position to pursue more investment opportunities.
              </li>
              <li>
                <b>Cryptocurrency Exchanges</b>: Need financing for their margin lending and trading
                services.
              </li>
            </ul>
          </Typography>
          <Typography variant="h5" className={classes.title2}>
            Are there any risks?
          </Typography>
          <Typography variant="body1">
            Like any financial investment, depositing your assets into a crypto savings account
            comes with risks.
          </Typography>
          <Typography variant="subtitle1" className={classes.subtitle}>
            Loan Default Risk
          </Typography>
          <Typography variant="body1">
            As we mentioned before, the risk of default on the borrower side is very limited because
            the loans are secured. When the value of the collateral backing the loans falls below a
            certain treshold, crypto lenders can sell the assets. However, in the event of a black
            swan event where market prices crash within minutes, the value of the collateral could
            fall below the loan value.
          </Typography>
          <Typography variant="subtitle1" className={classes.subtitle}>
            Custodian Hack
          </Typography>
          <Typography variant="body1">
            Perhaps the largest risk factor is that the lending company’s custody provider (where
            your assets are stored ) gets hacked. If an infiltrator penetrates a custodians’ wallet
            they could run away with the users’ money. To offset this risk, many custody providers
            have insurance policies to protect users in case they do get hacked. However, these
            often have an amount ceiling so whether you get reimbursed or not in the event of a hack
            depends on how much money was lost overall. Sometimes, the lending providers themselves,
            have additional insurance policies. If insurances exist, we always list them in the
            ‘details’ section
          </Typography>
          <Typography variant="subtitle1" className={classes.subtitle}>
            Smart contract vulnerability
          </Typography>
          <Typography variant="body1">
            Some of the providers listed above, namely Argent, ZenGo & Dharma use the decentralized{' '}
            <a href="https://cryptotesters.com/blog/what-is-decentralized-finance">
              Compound protocol
            </a>{' '}
            for their savings accounts. This means your assets are not being lent out by a company
            but an automated lending platform on the Ethereum blockchain. These smart contracts can
            have bugs that attackers can exploit to steal the money. All this being said, none of
            the scenarios mentioned have happened. If you want to read more on the crypto lending
            sector read our article on “How to think about savings rates in crypto?”.
          </Typography>
        </div>
      </div>
      <div className={classes.container}>
        <div className={classes.titleContainer}>
          <div className={classes.titleBackground} />
          <Typography variant="h5">How to choose a crypto savings account</Typography>
        </div>
        <div className={classes.features}>
          <div className={classes.feature}>
            <img
              width={40}
              height={40}
              src="/svg/usb-purple.svg"
              className={classes.icon}
              alt="lock icon"
            />
            <div>
              <Typography variant="subtitle1" className={classes.featureTitle}>
                Custodial
              </Typography>
              <Typography variant="body1">
                Some savings accounts are provided by centralized crypto companies while others are
                provided by DeFi protocols and smart contracts. The former are custodial meaning you
                lose control over your assets while with the latter you remain in control.
              </Typography>
            </div>
          </div>
          <div className={classes.feature}>
            <img
              width={40}
              height={40}
              src="/svg/shield.svg"
              className={classes.icon}
              alt="fees icon"
            />
            <div>
              <Typography variant="subtitle1" className={classes.featureTitle}>
                Security
              </Typography>
              <Typography variant="body1">
                Although security is a major concern for crypto lending providers their wallets,
                holding all their users’ funds, can potentially be hacked. DeFi protocols on the
                other hand can have flaws in their smart contracts. Therefore, we have listed
                whether providers have insurance policies for these types of events.
              </Typography>
            </div>
          </div>
          <div className={classes.feature}>
            <img
              width={40}
              height={40}
              src="/svg/bank-purple.svg"
              className={classes.icon}
              alt="liquidity icon"
            />
            <div>
              <Typography variant="subtitle1" className={classes.featureTitle}>
                KYC
              </Typography>
              <Typography variant="body1">
                Centralized providers have to identify their users to comply with regulations
                whereas DeFi providers don’t. To use a DeFi protocol such as Compound you only have
                to download a wallet which has integrated Compound, deposit your funds and you
                automatically start earning interest.
              </Typography>
            </div>
          </div>
          <div className={classes.feature}>
            <img
              width={40}
              height={40}
              src="/svg/ease.svg"
              className={classes.icon}
              alt="ease icon"
            />
            <div>
              <Typography variant="subtitle1" className={classes.featureTitle}>
                Convenience
              </Typography>
              <Typography variant="body1">
                Lastly, you want to find a provider that offers a decent user experience. Can you
                withdraw your assets any time or is there a lockup period? How often is the interest
                paid out? Is the app simple to use and does it have a delightful interface?
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </Box>
  )
}

export default SeoText
